import { Comment, DocumentNode, ProcedureDocument } from '@completion/models';
import { DocumentAction, DocumentActionTypes, LoadProcedureChecksSuccess, UpdateNodeSuccess } from '@completion/actions';
import isEqual from 'lodash/isEqual';
import { CommentStatus, DocumentStatus } from '@completion/enums';

export const initialState: DocumentState = {
  currentDocument: null,
  editMode: false,
  reviewerMode: false,
  status: DocumentStatus.CLEAN,
  procedureChecks: [],
};

export interface DocumentState {
  currentDocument: ProcedureDocument;
  editMode: boolean;
  reviewerMode: boolean;
  status: DocumentStatus;
  procedureChecks: DocumentNode[];
}

function addCommentToNodes(nodes: DocumentNode[] = [], nodeId: number, comment: Partial<Comment>): DocumentNode[] {
  return nodes.map((n) => {
    if (!n) return n;
    const updated: DocumentNode = { ...n };
    if (String(updated.id) === String(nodeId)) {
      const newComment: Comment = {
        // Prefer supplied id (e.g. from AddComment action); otherwise create a negative temp id
        id: (comment as any)?.id != null ? (comment as any).id : Date.now() * -1,
        content: comment.content || '',
        author: (comment as any).author, // expecting author injected upstream
        createdAt: (comment as any)?.createdAt || new Date().toISOString(),
        selectionText: (comment as any).selectionText,
        startOffset: (comment as any).startOffset,
        endOffset: (comment as any).endOffset,
        status: (comment as any).status || CommentStatus.CREATED,
      } as Comment;
      updated.comments = Array.isArray(updated.comments) ? [...updated.comments, newComment] : [newComment];
    }
    if (Array.isArray(updated.children) && updated.children.length) {
      updated.children = addCommentToNodes(updated.children, nodeId, comment);
    }
    return updated;
  });
}

export function documentReducer(state: DocumentState = initialState, action: DocumentAction): DocumentState {
  switch (action.type) {
    case DocumentActionTypes.DocumentLoadSuccess:
      // Avoid updating state if the document is unchanged to prevent reactive loops
      if (isEqual(state.currentDocument, action.document)) {
        return state;
      }
      return {
        ...state,
        currentDocument: action.document,
        status: DocumentStatus.CLEAN,
      };
    case DocumentActionTypes.DocumentCreate:
      if (isEqual(state.currentDocument, action.document)) {
        return state;
      }
      return {
        ...state,
        currentDocument: action.document,
        status: DocumentStatus.DIRTY,
      };
    case DocumentActionTypes.DocumentUpdate:
      if (isEqual(state.currentDocument, action.document)) {
        return state;
      }
      return {
        ...state,
        currentDocument: action.document,
        status: DocumentStatus.DIRTY,
      };
    case DocumentActionTypes.DocumentUpdateSuccess:
      return {
        ...state,
        currentDocument: action.document || state.currentDocument,
        status: DocumentStatus.SAVED,
      };
    case DocumentActionTypes.DocumentCreateSuccess:
      return {
        ...state,
        currentDocument: action.document || state.currentDocument,
        status: DocumentStatus.SAVED,
      };
    case DocumentActionTypes.ToggleEditMode:
      return {
        ...state,
        editMode: !state.editMode,
        reviewerMode: false, // turning on edit turns off reviewer mode
      };
    case DocumentActionTypes.SetEditMode:
      // Set editMode explicitly to the provided value; avoid toggling semantics which can lead to loops
      return {
        ...state,
        editMode: action.isEditMode,
        reviewerMode: action.isEditMode ? false : state.reviewerMode,
      };
    case DocumentActionTypes.ToggleReviewerMode:
      return {
        ...state,
        reviewerMode: !state.reviewerMode,
        editMode: false, // ensure mutual exclusivity
      };
    case DocumentActionTypes.SetReviewerMode:
      return {
        ...state,
        reviewerMode: action.isReviewerMode,
        editMode: action.isReviewerMode ? false : state.editMode,
      };
    case DocumentActionTypes.AddComment:
      if (!state.currentDocument) return state; // no doc loaded
      const { nodeId, comment } = action as any;
      const updatedNodes = addCommentToNodes(state.currentDocument.nodes, nodeId, comment);
      return {
        ...state,
        currentDocument: { ...state.currentDocument, nodes: updatedNodes },
        status: DocumentStatus.DIRTY,
      };
    case DocumentActionTypes.DocumentClear:
      return {
        ...state,
        currentDocument: null,
        editMode: false,
        reviewerMode: false,
        status: DocumentStatus.CLEAN,
        procedureChecks: [],
      };
    case DocumentActionTypes.LoadProcedureChecksSuccess:
      return {
        ...state,
        procedureChecks: (action as LoadProcedureChecksSuccess).checkTests || [],
      };
    case DocumentActionTypes.UpdateNodeSuccess:
      return {
        ...state,
        procedureChecks: replaceNodeInList(state.procedureChecks, (action as UpdateNodeSuccess).node),
      };
    default:
      return state;
  }
}

/** Replace a node by id, searching both the top level and children. */
function replaceNodeInList(nodes: DocumentNode[], updatedNode: DocumentNode): DocumentNode[] {
  if (!updatedNode || updatedNode.id == null) {
    return nodes;
  }
  return nodes.map((n) => {
    if (n.id === updatedNode.id) {
      return updatedNode;
    }
    if (n.children?.length) {
      const updatedChildren = n.children.map((c) => (c.id === updatedNode.id ? updatedNode : c));
      // Only create a new parent reference if a child actually changed
      if (updatedChildren.some((c, i) => c !== n.children[i])) {
        return { ...n, children: updatedChildren };
      }
    }
    return n;
  });
}
